import { AuthProvider } from 'react-admin';
import { IData } from '../types';
import { IDENTITY_URL } from '../serviceUrls';

function parseJwt (token: string): IData {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c: string) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

function log (...args: any[]): void {
    // Retrieve the stored output array from local storage
    const stored = JSON.parse(localStorage.getItem('consoleOutput') || '[]');
    const storedOutput = Array.isArray(stored) ? stored : [];

    // Add the new output (args array) to the array
    storedOutput.push({
        args,
        url: document.location.href,
        timestamp: new Date().getTime()
    });

    // Store the updated output array in local storage
    localStorage.setItem('consoleOutput', JSON.stringify(storedOutput));

    // Log the output to the console
    console.log(...args);
}


const getProfileFromToken = (tokenJson: string): IData => {
    const token = JSON.parse(tokenJson);
    const jwt = parseJwt(token.jwt);
    return { id: 'my-profile', ...jwt };
};

async function exchangeOTPforJWT (otp: string): Promise<IData> {
    const response = await fetch(IDENTITY_URL + '/jwt/exchange', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ otp })
    });
    if (!response.ok) {
        throw new Error(await response.text());
    }
    let data = await response.json();
    return data;
}

function fireAuthEvent (eventName: string): void {
    const event = new CustomEvent('devsoftAuthEvent', { detail: { authState: eventName } });
    document.dispatchEvent(event);
}

// ref: https://stackoverflow.com/questions/28230845/communication-between-tabs-or-windows
//
// Also remember that the current tab which broadcasts the message doesn't actually receive it, only other tabs or windows on the same domain.
//
window.addEventListener('storage', messageReceive);

function messageReceive (ev: StorageEvent): void {
    if (ev.key === 'token') {
        log('message_receive: token changed. Reason: From another tab or window');
        fireAuthEvent('authenticated');
    }
}

export interface SongtekAuthProvider extends AuthProvider {
    login: (params: { otp: string }) => Promise<{ redirectTo: string }>;
    logout: () => Promise<string>;
    checkAuth: () => Promise<void>;
    checkError: (error: { status?: number }) => Promise<void>;
    getIdentity: () => Promise<{ id: string; fullName: string }>;
    getPermissions: () => Promise<string>;
    getJWT: () => string;
}

const authProvider: SongtekAuthProvider = {
    login: async ({ otp }) => {
        if (!otp) {
            throw new Error('Unset OTP');
        }
        log('login: exchangeOTPforJWT and store token into local storage');
        // Transform the code to a token via the API
        const token = await exchangeOTPforJWT(otp);
        localStorage.setItem('token', JSON.stringify(token));
        // cleanup();
        fireAuthEvent('authenticated');
        return Promise.resolve({ redirectTo: '/' });
    },
    logout: () => {
        log('logout: remove token from local storage');
        localStorage.removeItem('token');
        sessionStorage.clear();
        // debugger;
        return Promise.resolve('');
        // return Promise.resolve('https://hello2.songtek.net/Logout');
    },
    checkAuth: () => {
        const token = localStorage.getItem('token');

        if (!token) {
            // debugger;
            log('checkAuth: false. Reason: token not found in local storage');
            return Promise.reject();
        }

        // This is specific to the Google authentication implementation
        const jwt = getProfileFromToken(token);
        const now = new Date();
        const hasExpired = now.getTime() > (jwt.exp * 1000);
        log(`checkAuth: ${hasExpired}. Reason: check hasExpired=${hasExpired}`);
        if (hasExpired) {
            fireAuthEvent('unauthenticated');
            return Promise.resolve();
        }
        fireAuthEvent('authenticated');
        return Promise.resolve();
    },
    checkError: (error) => {
        const status = error.status;
        if (status === 401 /*|| status === 403*/) {
            log('checkError: Asking for relogin. Reason: ' + status);
            fireAuthEvent('unauthenticated');
            return Promise.resolve(); // let continue as normal
        }
        if (status === 403) {
            log('checkError: redirect to /error/403. Reason: ' + status);
            return Promise.reject({ redirectTo: '/error/403', logoutUser: false });
        }
        log('checkError: other error code (404, 500, etc): no need to log out. Reason: ' + status);
        // other error code (404, 500, etc): no need to log out
        return Promise.resolve();
    },
    getIdentity: () =>
        Promise.resolve({
            id: 'user',
            fullName: 'John Doe',
        }),
    getPermissions: () => Promise.resolve(''),
    getJWT: () => {
        const tokenJson = localStorage.getItem('token');
        if (!tokenJson) {
            throw new Error('Not logged in');
        }
        const token = JSON.parse(tokenJson);
        return token.jwt;
    },
};

export default authProvider;
